import { assertInInjectionContext, inject } from '@angular/core';
import { patchState, signalStoreFeature, withHooks, withMethods, withState } from '@ngrx/signals';
import { AuthService } from '../auth/auth.service';
import { AccountStateService } from '../account/state/account-state.service';
import { EnvironmentCustomerType } from '@mwe/environment';
import { EnvironmentService } from '../environment/environment.service';
import { AccountInfoStatus, IBulletConfirmationStatusChangeResponse } from '@mwe/models';

const initialState = {
  isLoggedIn: false as boolean,
  hasAccount: false as boolean,
  isImpersonating: false as boolean,
  isPartner: false as boolean,
  portalCustomerType: undefined as EnvironmentCustomerType,
  autoRegistrationSuccessful: undefined as boolean,
  isNewAccountCreated: false as boolean,
  isNewAccountCreationError: false as boolean,
  finishedAccountCreation: false as boolean,
};

type AuthenticationState = typeof initialState;

export function withAuthenticationStateFeature() {
  return signalStoreFeature(
    withState<AuthenticationState>(initialState),
    withMethods(store => {
      /** Detect the current authentication state from the AuthService */
      function detectCurrentAuthenticationStateInInjectionContext(): void {
        assertInInjectionContext(detectCurrentAuthenticationStateInInjectionContext);
        const authService = inject(AuthService);
        const isAuthenticated = authService.isAuthenticated();
        patchState(store, {
          isLoggedIn: isAuthenticated,
          isImpersonating: inject(AccountStateService).isImpersonate(),
          isPartner: isAuthenticated,
          hasAccount: isAuthenticated,
          portalCustomerType: inject(EnvironmentService).getPortalCustomerType(),
          autoRegistrationSuccessful: false,
          finishedAccountCreation: false,
        });
      }

      function checkConfirmationStatusResponseForAccountCreation(response: IBulletConfirmationStatusChangeResponse) {
        // Update the store based on the confirmation status response
        try {
          const responseBody = JSON.parse(response.responseData);
          const accountStatus = responseBody.accountStatus ?? responseBody;
          let initialAccountInfo = accountStatus.initialAccountInfo;
          const accountInfo: AccountInfoStatus = accountStatus.accountInfo;
          if (typeof initialAccountInfo === 'undefined') initialAccountInfo = accountInfo;

          // We consider auto-registration successful if the accountInfo has changed from NO_ACCOUNT (0) to HAS_ACCOUNT (1) or IS_PARTNER (2) and not to CREATE_ERROR (3)
          const registrationSuccessful = accountInfo >= initialAccountInfo && accountInfo < AccountInfoStatus.CREATE_ERROR;
          const hasAccount = accountInfo >= AccountInfoStatus.HAS_ACCOUNT && accountInfo < AccountInfoStatus.CREATE_ERROR;
          const isPartner = accountInfo >= AccountInfoStatus.IS_PARTNER && accountInfo < AccountInfoStatus.CREATE_ERROR;

          patchState(store, {
            autoRegistrationSuccessful: registrationSuccessful,
            hasAccount,
            isPartner,
            isNewAccountCreated: registrationSuccessful,
            isNewAccountCreationError: accountInfo === AccountInfoStatus.CREATE_ERROR,
            finishedAccountCreation: true,
          });
        } catch {}
      }

      return {
        checkConfirmationStatusResponseForAccountCreation,
        detectCurrentAuthenticationStateInInjectionContext,
      };
    }),
    withHooks({
      onInit: store => {
        store.detectCurrentAuthenticationStateInInjectionContext(); // Initialize the authentication state on store creation and detect current state
      },
    }),
  );
}
